import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Tooltip from 'react/components/common/Tooltip/Tooltip';

/**
 * CategoryGroupRow - Collapsible parent row for Level 2 category groups
 * Displays group name and metrics from the child with highest Audience Skew
 * DMP Category ID and Audience Reach columns are empty for group rows
 */
const CategoryGroupRow = ({ group, isExpanded, onToggle, columns }) => {
  const handleToggle = (e) => {
    e.stopPropagation();
    onToggle(group.id);
  };

  // Render cell content based on column
  const renderCell = (column) => {
    switch (column.id) {
      case 'phrase':
        return (
          <div className="category-group-name">
            <span className={classNames('toggle-icon', { expanded: isExpanded })} onClick={handleToggle}>
              <i className={classNames('icon', isExpanded ? 'icon-Triangle_Down' : 'icon-Triangle_Right')}></i>
            </span>
            <span className="group-name-text">{group.groupName}</span>
            <span className="children-count">({group.children.length})</span>
          </div>
        );

      case 'interest-portion': {
        // Category Portion In Audience
        const portion = (group['interest-portion'] || 0).toFixed(2);
        const tooltipText = `${portion}% of selected audience is included in ${group.groupName}`;
        return (
          <Tooltip content={tooltipText}>
            <span>{portion}%</span>
          </Tooltip>
        );
      }

      case 'segment-portion': {
        // Audience Portion In Category
        const portion = (group['segment-portion'] || 0).toFixed(2);
        const tooltipText = `${portion}% of the users in ${group.groupName} are in the selected audience`;
        return (
          <Tooltip content={tooltipText}>
            <span>{portion}%</span>
          </Tooltip>
        );
      }

      case 'uniqueness-index': {
        // Audience Skew
        const skew = group['uniqueness-index'] || 0;
        const tooltipText = `Users in ${group.groupName} are x${skew.toFixed(
          2
        )} times more likely to be in the target audience than the average user`;
        return (
          <Tooltip content={tooltipText}>
            <span>{skew.toFixed(2)}</span>
          </Tooltip>
        );
      }

      case 'reach':
        // Audience Reach - empty for group rows
        return <span className="empty-cell"></span>;

      case 'id':
        // DMP Category ID - empty for group rows
        return <span className="empty-cell"></span>;

      default:
        return null;
    }
  };

  return (
    <div className={classNames('category-group-row', { expanded: isExpanded })} onClick={handleToggle}>
      {columns.map((column) => (
        <div key={column.id} className={classNames('column', `col-${column.id}`, `flex${column.flex || '1'}`)}>
          {renderCell(column)}
        </div>
      ))}
    </div>
  );
};

CategoryGroupRow.propTypes = {
  group: PropTypes.shape({
    id: PropTypes.string.isRequired,
    groupName: PropTypes.string.isRequired,
    children: PropTypes.array.isRequired,
    'uniqueness-index': PropTypes.number,
    'interest-portion': PropTypes.number,
    'segment-portion': PropTypes.number,
  }).isRequired,
  isExpanded: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  columns: PropTypes.array.isRequired,
};

export default CategoryGroupRow;
